import { FC } from 'react';
import { Field, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  DynamicHeading,
  getContrastThemeClass,
  resolveColorProperty,
  SitecoreFieldTypes,
} from '@gn-audio/jss-components';

import { mapSharedPropertiesToFields } from 'common/getters';
import { withTheme } from 'common/jabraTheme/withTheme';
import { usePlaceholderContainer } from 'components/Product/KSP/Placeholder/PlaceholderContainerContext';
import { ComponentProps } from 'lib/component-props';

export type TextSpotFields = {
  headline: Field<string>;
  contentFontSize: Field<number>;
  centerText: Field<boolean>;
  content: Field<string>;
  backgroundColor: Field<string>;
  isMainHeading: Field<boolean>;
};
export type TextSpotProps = {
  fields: TextSpotFields;
} & ComponentProps;

const DEFAULT_CONTENT_FONT_SIZE = 18;

const TextSpotBase: FC<TextSpotProps> = (rendering) => {
  const { backgroundColor, centerText, headline, content, contentFontSize, isMainHeading } =
    mapSharedPropertiesToFields<TextSpotFields>(rendering.fields as SitecoreFieldTypes);

  const { isInsidePlaceholder } = usePlaceholderContainer();

  const hasCustomBackground = !!backgroundColor?.value;

  const backgroundColorHex = resolveColorProperty(backgroundColor?.value?.toLowerCase());

  const containerClass = classNames(
    { 'bg-theme': !hasCustomBackground && !isInsidePlaceholder },
    { 'bg-transparent': isInsidePlaceholder && !backgroundColorHex }
  );

  const insidePlaceholderTheme = backgroundColorHex
    ? getContrastThemeClass(backgroundColorHex)
    : undefined;

  return (
    <div
      className={classNames(containerClass, insidePlaceholderTheme)}
      style={backgroundColorHex ? { backgroundColor: backgroundColorHex } : undefined}
    >
      <div className="container flex max-w-6xl justify-center">
        <div
          className={classNames('flex w-full flex-col gap-3', centerText?.value && 'text-center')}
        >
          {headline?.value && (
            <DynamicHeading
              className={classNames(isMainHeading?.value ? 'heading-1' : 'heading-2')}
              isMainHeading={isMainHeading?.value}
            >
              {headline.value}
            </DynamicHeading>
          )}
          {content?.value && (
            <div
              style={{
                fontSize: `${contentFontSize?.value || DEFAULT_CONTENT_FONT_SIZE}px`,
              }}
              dangerouslySetInnerHTML={{
                __html: sanitize(content.value),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const TextSpot: FC<TextSpotProps> = (props) => {
  const { isInsidePlaceholder } = usePlaceholderContainer();

  const ThemedComponent = withTheme({ legacyBackgroundFieldName: 'backgroundColor' })(
    withDatasourceCheck()<TextSpotProps>(TextSpotBase)
  );

  const UnthemedComponent = withDatasourceCheck()<TextSpotProps>(TextSpotBase);

  return isInsidePlaceholder ? <UnthemedComponent {...props} /> : <ThemedComponent {...props} />;
};

export default TextSpot;
